import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Box, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import {
  motion,
  useAnimation,
  useMotionValue,
  ValueAnimationTransition,
} from 'framer-motion';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import { RiveAnimation, RiveAnimationRef } from '@frontend-libs/portal';

import { useLoading } from '@frontend/utils';

export interface PullToRefreshProps {
  operationKeys: string[];
}
const PULL_THRESHOLD = 50;
const CONTAINER_HEIGHT = 60;
const TRANSITION_RESET: ValueAnimationTransition<number> = {
  duration: 0.1,
  ease: 'easeOut',
};
const TRANSITION_START: ValueAnimationTransition<number> = {
  type: 'spring',
  stiffness: 300,
  damping: 20,
};

export const PullToRefresh = (props: PropsWithChildren<PullToRefreshProps>) => {
  const isNative = Capacitor.isNativePlatform();
  const riveRef = useRef<RiveAnimationRef>(null);
  const [isRefreshAnimationComplete, setIsRefreshAnimationComplete] =
    useState(false);
  const controls = useAnimation();
  const startYPosition = useRef<number>(0);
  const startXPosition = useRef<number>(0);
  const isPulling = useRef<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  // The Rive view is mounted only while the strip shows: an idle one keeps redrawing the page every frame.
  const [isStripShown, setIsStripShown] = useState<boolean>(false);
  const { operationsInProgress, reload } = useLoading();
  const theme = useTheme();
  const pullDistance = useMotionValue(0);
  const isHapticsTriggered = useRef(false);

  useEffect(() => {
    if (!isNative) {
      return;
    }

    return pullDistance.on('change', async (distance) => {
      if (isRefreshing) {
        return;
      }

      handleTrigger('distance', distance * 2);
      if (distance * 2 >= 100 && !isHapticsTriggered.current) {
        isHapticsTriggered.current = true;
        await Haptics.impact({ style: ImpactStyle.Medium });
      }

      if (distance * 2 < 100 && isHapticsTriggered.current) {
        isHapticsTriggered.current = false;
      }
    });
  }, [pullDistance, isNative]);

  const handleTrigger = (inputName: string, value?: number | boolean) =>
    riveRef.current?.trigger(inputName, value);

  const resetAnimation = () => {
    handleTrigger('distance', 0);
    handleTrigger('success', false);
    setIsRefreshAnimationComplete(false);
    setIsRefreshing(false);
    setIsStripShown(false);
  };

  const onCompleteLoading = async () => {
    if (!isRefreshing) return;

    await controls.start({ height: 0, transition: TRANSITION_RESET });
    resetAnimation();
  };

  useMemo(async () => {
    if (!isRefreshing) {
      return;
    }

    if (!props.operationKeys.some((key) => operationsInProgress.has(key))) {
      if (!isRefreshAnimationComplete) {
        handleTrigger('distance', 0);
        handleTrigger('success', true);
      }

      if (isRefreshAnimationComplete) {
        onCompleteLoading().catch((error) => {
          console.error(error);
          captureException(error);
        });
      }
    }
  }, [operationsInProgress, isRefreshAnimationComplete]);

  const isEventInsideDrawer = (e: React.TouchEvent<HTMLElement>): boolean => {
    const target = e.target as HTMLElement;
    return target.closest('#drawer-bottom') !== null;
  };

  const isEventOnSwiper = (e: React.TouchEvent<HTMLElement>): boolean => {
    const target = e.target as HTMLElement;
    return target.closest('.swiper') !== null;
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    if (
      isEventInsideDrawer(e) ||
      e.currentTarget.scrollTop > 0 ||
      isRefreshing
    ) {
      isPulling.current = false;
      return;
    }

    startYPosition.current = e.touches[0]?.clientY ?? 0;
    startXPosition.current = e.touches[0]?.clientX ?? 0;
    isPulling.current = true;
  };

  const handleTouchMove = async (e: React.TouchEvent<HTMLElement>) => {
    if (!isPulling.current || isRefreshing) {
      return;
    }

    const currentY = e.touches[0]?.clientY ?? 0;
    const currentX = e.touches[0]?.clientX ?? 0;
    const distanceY = Math.floor(currentY - startYPosition.current);
    const distanceX = Math.floor(Math.abs(currentX - startXPosition.current));

    if (distanceY > 10) {
      const distanceRatio = distanceY / distanceX; // If it's less than 0.75, assume it's horizontal swiping/pulling.

      // Workaround to avoid wonky behaviour with pull to refresh when swiping cards
      if (isEventOnSwiper(e) && distanceRatio < 0.75) {
        await controls.start({ height: 0, transition: TRANSITION_RESET });
        pullDistance.set(0);
        isPulling.current = false;
        setIsStripShown(false);
        return;
      }
      // Cap the pull distance
      const value = Math.min(distanceY, CONTAINER_HEIGHT);

      if (value === pullDistance.get()) {
        return;
      }

      setIsStripShown(true);
      controls.set({ height: value });
      pullDistance.set(value);
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling.current) {
      return;
    }

    isPulling.current = false;

    if (isRefreshing || !pullDistance.get()) {
      return;
    }

    if (pullDistance.get() >= PULL_THRESHOLD && !isRefreshing) {
      handleTrigger('release');
      setIsRefreshing(true);
      reload();
      await controls.start({
        height: PULL_THRESHOLD,
        transition: TRANSITION_START,
      });
      pullDistance.set(0);
    } else {
      // Reset back to initial position
      if (pullDistance.get() > 9) {
        handleTrigger('release');
      }
      await controls.start({ height: 0, transition: TRANSITION_RESET });
      resetAnimation();
      pullDistance.set(0);
    }
  };

  // The OS cancelled the gesture (edge swipe, call overlay, backgrounding):
  // close the strip without refreshing so the Rive view unmounts again.
  const handleTouchCancel = async () => {
    if (!isPulling.current) {
      return;
    }

    isPulling.current = false;
    await controls.start({ height: 0, transition: TRANSITION_RESET });
    resetAnimation();
    pullDistance.set(0);
  };

  if (!isNative) {
    return <>{props.children}</>;
  }

  return (
    <Box
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        height: ['100dvh', '100vh'],
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
      }}
    >
      <motion.div
        animate={controls}
        style={{
          backgroundColor: theme.palette.background.paper,
          height: 0,
          maxHeight: CONTAINER_HEIGHT,
        }}
      >
        <Box
          sx={{
            height: PULL_THRESHOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            width: '100%',
          }}
        >
          {isStripShown && (
            <RiveAnimation
              onEvent={(e: any) => {
                if (e.data.name === 'complete') {
                  setIsRefreshAnimationComplete(true);
                }
              }}
              ref={riveRef}
              src="/rive/pull_to_refresh.riv"
              height={48}
              width={48}
            />
          )}
        </Box>
      </motion.div>
      <>{props.children}</>
    </Box>
  );
};
